import { memo, FC, useState, useCallback, useRef, useEffect } from "react";
import { ScrollView } from "react-native";
import { PressableScale } from "pressto";
import { View, Text, ScreenContainer, Card } from "@src/components";
import { type IconNameType } from "@src/components/Icon";
import { WeeklyRoutineGrid, createWeeklyStore } from "../Grids/WeeklyRoutineGrid";
import { RotatingRoutineGrid, createRotatingStore } from "../Grids/RotatingRoutineGrid";
import { GranularitySelector } from "../Grids/GranularitySelector";
import { useNumDays, type GridStore } from "../Grids/grid-store";
import { MAX_VISIBLE_DAYS } from "../Grids/constants";
import { WeeklyAlarmRoutine, RotatingAlarmRoutine } from "@visioni/a3s-alarm-schedules/schema";
import { StyleSheet } from "react-native-unistyles";
import { isWeb } from "@src/utils/web-helpers";
import SegmentedSelector from "@src/components/SegmentedSelector";
import { StartDateSection } from "../Grids/RotatingRoutineGrid/StartDateSection";
import { CycleLengthSection } from "../Grids/RotatingRoutineGrid/CycleLengthSection";
import { GridActions } from "./GridActions";

export type RoutineMode = "weekly" | "rotating";

interface RoutineCustomizationProps {
  onCancel?: () => void;
  onSave: (routine: WeeklyAlarmRoutine | RotatingAlarmRoutine) => void;
  initialRoutine?: WeeklyAlarmRoutine | RotatingAlarmRoutine;
  showHeader?: boolean;
}

export const RoutineCustomization: FC<RoutineCustomizationProps> = memo(
  ({ onCancel, onSave, initialRoutine, showHeader = true }) => {
    const [mode, setMode] = useState<RoutineMode>(initialRoutine?.kind || "weekly");

    const weeklyStoreRef = useRef<GridStore | null>(null);
    const rotatingStoreRef = useRef<GridStore | null>(null);
    if (mode === "weekly" && !weeklyStoreRef.current) {
      weeklyStoreRef.current = createWeeklyStore(
        initialRoutine?.kind === "weekly" ? (initialRoutine as WeeklyAlarmRoutine) : undefined,
      );
    }
    if (mode === "rotating" && !rotatingStoreRef.current) {
      rotatingStoreRef.current = createRotatingStore(
        initialRoutine?.kind === "rotating" ? (initialRoutine as RotatingAlarmRoutine) : undefined,
      );
    }
    const activeStore = (mode === "weekly" ? weeklyStoreRef.current : rotatingStoreRef.current)!;

    const onSaveRef = useRef(onSave);

    // Keep the ref updated
    useEffect(() => {
      onSaveRef.current = onSave;
    }, [onSave]);

    // Notify parent of initial routine data when it's provided
    useEffect(() => {
      if (initialRoutine) {
        onSaveRef.current(initialRoutine);
      }
    }, [initialRoutine]);

    // Handle routine data changes — forward directly to parent via ref, no local state needed
    const handleDataChange = useCallback((routine: WeeklyAlarmRoutine | RotatingAlarmRoutine | null) => {
      if (routine) {
        onSaveRef.current(routine);
      }
    }, []);

    const modeOptions: { title: string; icon: IconNameType }[] = [
      { title: "Weekly Routine", icon: "calendarOutline" },
      { title: "Rotating Pattern", icon: "rotate" },
    ];

    const handleModeChange = useCallback((option: string) => {
      if (option === "Weekly Routine") {
        setMode("weekly");
      } else if (option === "Rotating Pattern") {
        setMode("rotating");
      }
    }, []);

    const numDays = useNumDays(activeStore);
    const needsPagination = mode === "rotating" && numDays > MAX_VISIBLE_DAYS;

    return (
      <ScreenContainer edges={showHeader ? ["top"] : []}>
        {/* Header */}
        {showHeader && (
          <View
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            px="5xl"
            pb="3xl"
            bg="background"
          >
            {onCancel && (
              <PressableScale onPress={onCancel}>
                <Text textStyle="md/regular" color="text">
                  Cancel
                </Text>
              </PressableScale>
            )}
            <Text textStyle="xl" fontWeight="600">
              Customise Routine
            </Text>
            <View style={{ width: 60 }} />
          </View>
        )}

        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.contentContainerStyle}
          showsVerticalScrollIndicator={false}
        >
          <Card px="xl" py="xl" mb="xl" style={styles.card}>
            {/* Mode Toggle */}
            <View pb="xl">
              <SegmentedSelector
                optionsLabels={modeOptions}
                selectedOption={mode === "weekly" ? "Weekly Routine" : "Rotating Pattern"}
                onSelectOption={handleModeChange}
                width={isWeb ? 360 : 350}
                containerStyle={styles.segmentedSelectorContainer}
                activeBoxStyle={styles.segmentedSelectorActiveBox}
                textColorActiveBox="white"
              />
            </View>

            {/* Routine Controls */}
            <View>
              {mode === "rotating" && (
                <>
                  <StartDateSection store={activeStore} />
                  <CycleLengthSection store={activeStore} />
                </>
              )}
              <GranularitySelector store={activeStore} />
            </View>
          </Card>

          {/* Routine Grid */}
          <Card px="xl" py="xl" style={styles.card}>
            <View>
              <Text textStyle="xs/semibold">Select hours</Text>
              <Text textStyle="xs/regular" color="textSecondary">
                Tap cells to select hours to arm. Untap to disarm.
              </Text>
            </View>
            {mode === "weekly" ?
              <WeeklyRoutineGrid
                store={activeStore}
                onDataChange={handleDataChange}
                initialData={mode === initialRoutine?.kind ? (initialRoutine as WeeklyAlarmRoutine) : undefined}
              />
            : <RotatingRoutineGrid
                store={activeStore}
                onDataChange={handleDataChange}
                initialData={mode === initialRoutine?.kind ? (initialRoutine as RotatingAlarmRoutine) : undefined}
              />
            }

            <GridActions store={activeStore} needsPagination={needsPagination} />
          </Card>

          {/* Armed / Disarmed legend */}
          <View pt="3xl" pb="4xl" bg="background">
            <View bg="surface" p="4xl" rounded={8} borderWidth={1} borderColor="border" gap="2xl">
              <View flexDirection="row" alignItems="center" gap="4xl">
                <View flexDirection="row" alignItems="center" gap="sm">
                  <View w={12} h={12} rounded={6} style={styles.armedLegendDot} />
                  <Text textStyle="xs/regular" color="text">
                    Armed
                  </Text>
                </View>
                <View flexDirection="row" alignItems="center" gap="sm">
                  <View w={12} h={12} rounded={6} style={styles.disarmedLegendDot} />
                  <Text textStyle="xs/regular" color="text">
                    Disarmed
                  </Text>
                </View>
              </View>
              <Text textStyle="xs/regular" color="textMuted">
                Tap or drag over cells to toggle between armed and disarmed times.
              </Text>
            </View>
          </View>
        </ScrollView>
      </ScreenContainer>
    );
  },
);

const styles = StyleSheet.create((theme) => ({
  scrollView: {
    flex: 1,
  },
  contentContainerStyle: {
    paddingBottom: theme.spacing["5xl"],
    paddingHorizontal: theme.spacing["xl"],
  },
  segmentedSelectorContainer: {
    height: 40,
    backgroundColor: theme.colors.surface1.background,
    borderColor: theme.colors.field.border_default,
  },
  segmentedSelectorActiveBox: {
    backgroundColor: theme.colors.surface1.hover,
  },
  card: {
    backgroundColor: theme.colors.surface1.background,
  },
  armedLegendDot: {
    backgroundColor: theme.colors.alarmStates.armed,
  },
  disarmedLegendDot: {
    backgroundColor: theme.colors.alarmStates.disarmed,
  },
}));
